/**
 * View component for /module-certificates
 *
 * Generic moduleCertificate list view. Defaults to 'all' with:
 * this.props.dispatch(moduleCertificateActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, withRouter, NavLink } from 'react-router-dom';

// import actions
import * as moduleCertificateActions from '../moduleCertificateActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as studyActions from '../../study/studyActions'; 

// import third-party libraries
import _ from 'lodash';
import classNames from 'classnames';

// import global components
import Base from '../../../global/components/BaseComponent.js.jsx';
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';

// import moduleCertificate components
import ModuleCertificateLayout from '../components/ModuleCertificateLayout.js.jsx';
import ModuleCertificateListItem from '../components/ModuleCertificateListItem.js.jsx';
import ModuleCertCard from '../../agenda/components/ModuleCertCard.js.jsx';
import OtherCertCard from '../../agenda/components/OtherCertCard.js.jsx';

// import moduleCertificate css modules
import moduleCertificateStyles from '../moduleCertificateModuleStyles.css';

// import utilities
import certUtils from '../../../global/utils/certUtils';
import filterUtils from '../../../global/utils/filterUtils';

// not updating links here because this should no longer be used with london handling agenda and certs

class ModuleCertificateList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      infoBoxStyle: 'centered-info-box'
      , queryText: ''
      , cmIds: []
    }
    this._bind(
      '_handleQuery'
    )
  }

  componentDidMount() {
    const { loggedInUser, dispatch, moduleCertificateStore } = this.props;

    // old page - redirect to london certs view just in case
    window.location.replace(`${window.agendaUrl}/certificates`);
    
    // fetch a list of your choice  
    dispatch(studyActions.fetchListIfNeeded('user', loggedInUser._id)); // fetch studies for user to check if they have memberContent access
    dispatch(moduleCertificateActions.fetchListIfNeeded('user', loggedInUser._id)).then((action) => { // new api for "user" arg (not _user) so we ignore excluded, revoked, or exempt certs
      let cmIds = action.list.map(item => item._contentModule);
      this.setState({cmIds});
      dispatch(contentModuleActions.fetchListIfNeeded('_id', cmIds));
    });
    dispatch(contentModuleActions.fetchListIfNeeded('agenda-status', 'expired'));
    dispatch(contentModuleActions.fetchListIfNeeded('agenda-status', 'expiring-soon'));
    dispatch(contentModuleActions.fetchListIfNeeded('agenda-status', 'pending-attestation'));
    dispatch(contentModuleActions.fetchListIfNeeded('agenda-status', 'completed'));
    // hide the infoBox.
    setTimeout(() => {
      this.setState({
        infoBoxStyle: 'centered-info-box -hide'
      })
    }, 3500)
  }

  _handleQuery(e) {
    this.setState({
      [e.target.name]: e.target.value.toLowerCase()
    });
  }

  render() {
    const {
    
      location
      , loggedInUser
      , match
      , moduleCertificateStore
      , contentModuleStore
      , studyStore
    } = this.props;
    const { infoBoxStyle, queryText, cmIds } = this.state;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    // get the moduleCertificateList meta info here so we can reference 'isFetching'
    const moduleCertificateList = moduleCertificateStore.lists.user ? moduleCertificateStore.lists.user[loggedInUser._id] : {};

    /**
     * use the reducer getList utility to convert the all.items array of ids
     * to the actual moduleCertificate objetcs
     */
    const moduleCertificates = moduleCertificateStore.util.getList('user', loggedInUser._id) || [];

    // TODO: other statuses
    let validCerts = moduleCertificates.filter(cert => certUtils.checkIfValid(cert))
    // 1.12 - remove excluded certs from the list
    validCerts = validCerts.filter(cert => cert.status !== "excluded")

    let invalidCerts = moduleCertificates.filter(cert => !certUtils.checkIfValid(cert))
    // 1.12 - remove invalidated certs from the list
    invalidCerts = invalidCerts.filter(cert => cert.status !== "invalidated")

    const contentModules = contentModuleStore.util.getList('_id', cmIds);

    const expiringSoonList = contentModuleStore.lists['agenda-status'] ? contentModuleStore.lists['agenda-status']['expiring-soon'] : {};
    const expiringSoonItems = expiringSoonList ? expiringSoonList.agendaItems : []; 
    const pendingList = contentModuleStore.lists['agenda-status'] ? contentModuleStore.lists['agenda-status']['pending-attestation'] : {};
    const pendingItems = pendingList ? pendingList.agendaItems : []; 
    const expiredList = contentModuleStore.lists['agenda-status'] ? contentModuleStore.lists['agenda-status']['expired'] : {};
    const expiredItems = expiredList ? expiredList.agendaItems : [];
    const completedList = contentModuleStore.lists['agenda-status'] ? contentModuleStore.lists['agenda-status']['completed'] : {};
    const completedItems = completedList ? completedList.agendaItems : [];

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = (
      !moduleCertificates
      || !moduleCertificates.length > 0
      || !moduleCertificateList
      || !contentModules
      || contentModules.length === 0
      || !expiredItems
      || !pendingItems
      || !expiringSoonItems
      || !completedItems
    );

    const isFetching = (
      moduleCertificateList && moduleCertificateList.isFetching 
      || expiringSoonList && expiringSoonList.isFetching
      || pendingList && pendingList.isFetching
      || expiredList && expiredList.isFetching
      || completedList && completedList.isFetching
    )

    const attentionItems = !isEmpty && !isFetching && expiredItems.concat(pendingItems, expiringSoonItems); 

    // filter out actionable and completed agenda items for studies/groups that don't have memberContent access
    // separate them out and put them in miscellaneous cert list to later concat with nonAgendaCerts
    const otherCerts = []; 
    const filterByStudyAccess = (items = []) =>
      items.filter(item => {
        const study = studyStore.byId[item._group];
        if (study && study.memberContent && study.memberContent.hasAccess) {
          return true;
        }
        if (item._certificate) {
          otherCerts.push(item._certificate);
        }
        return false;
      });
    const filteredAttentionItems = attentionItems && filterByStudyAccess(attentionItems); 
    const filteredCompletedItems = completedItems && filterByStudyAccess(completedItems); 
    
    // 2.7 - display all other certs for a user even from deactivated group or deactivated groupUser 
    const allAgendaItems = !isEmpty && !isFetching && filteredAttentionItems.concat(completedItems); 
    // get content module ids from all agendaItems
    const itemCMIds = allAgendaItems && allAgendaItems.map(item => item._contentModule);
    // isolate certs that are not covered by agendaItems
    const nonAgendaCerts = itemCMIds && moduleCertificates.filter(cert => !itemCMIds.includes(cert._contentModule));

    // combine nonAgendaCerts with otherCerts in groups without memberContent and no duplicates 
    const allOtherCerts = Array.from(
      new Map([...nonAgendaCerts, ...otherCerts].map(item => [item._id, item])).values()
    );

    return (
      <ModuleCertificateLayout>        
        <Helmet
          title={"My Certificates | Pro-ficiency"}
        />
        <div className="flex">
          <section className="section my-agenda">
            <div className="-header" style={{marginBottom: 10}}>
              <h2>
                <i className="fa-regular fa-graduation-cap fa-sm" style={{paddingRight: 10}}></i>
                Training
              </h2>
            </div>
            { loggedInUser && loggedInUser.username ?
              <div className="yt-row" style={{marginBottom: '20px', marginTop: '20px'}}>
                <div className="-tab-wrapper">
                  <NavLink to="/my-agenda" exact={true} className={`-tab-btn`}>My Agenda</NavLink>
                </div>
                <div className="-tab-wrapper">
                  <NavLink to="/my-certificates" className={`-tab-btn active`}>Certificates</NavLink>
                </div>
              </div>
              :
              null
            }
            { isEmpty ?
              (isFetching ?
                <div className="yt-container" style={{marginTop: '20px'}}>
                  <div className="u-centerText">
                    <div className="loading"></div>
                  </div>
                </div>
              :
                <div className="yt-container">
                  <div className="yt-row center-horiz">
                    <div className="empty-wrapper">
                      <i className="fa-regular fa-file-certificate fa-2xl"></i>
                      <h2>No certificates yet!</h2>
                      <div style={{marginBottom: '32px', marginTop: '32px'}}>
                        <p className="-empty-message">Certificates from completed modules will be shown here, serving as a record of your compliance. </p>
                      </div>
                      <div style={{marginBottom: '32px'}}>
                        <p className="-contact-message">If you expected to see certificates here, please contact your administrator</p>
                        <p className="-contact-message">For additional help contact support <Link to="/contact-us" className="-contact-link">here.</Link></p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            :
            <div>
              {filteredAttentionItems && filteredAttentionItems.length > 0 ? 
                <div style={{marginTop: '40px', marginBottom:'20px'}}>
                  <h3 style={{marginBottom: '20px'}}>Action Items</h3>
                  <div className="yt-row with-gutters">
                    {filteredAttentionItems.map((agendaItem, i) => 
                      <ModuleCertCard
                        agendaItem={agendaItem}
                        key={'optInc_' + agendaItem._contentModule + '_' +i }
                        contentModule={contentModuleStore.byId[agendaItem._contentModule]}
                      /> 
                    )}
                  </div>
                </div>
              : null
              }
              {filteredCompletedItems && filteredCompletedItems.length > 0 ? 
                <div style={{marginTop: '40px'}}>
                  <h3 style={{marginBottom: '20px'}}>Certificates</h3>
                  <div className="yt-row with-gutters">
                    {filteredCompletedItems.map((agendaItem, i) => 
                      <ModuleCertCard
                        agendaItem={agendaItem}
                        key={'optInc_' + agendaItem._contentModule + '_' +i }
                        contentModule={contentModuleStore.byId[agendaItem._contentModule]}
                      /> 
                    )}
                  </div>
                </div>
              : null
              }
              {allOtherCerts && allOtherCerts.length > 0 ? 
                <div style={{marginTop: '40px'}}>
                  <h3 style={{marginBottom: '20px'}}>Miscellaneous</h3>
                  <div className="yt-row with-gutters">
                    {allOtherCerts.map((cert, i) => 
                      <OtherCertCard
                        certificate={cert}
                        key={cert._id}
                      /> 
                    )}
                  </div>
                </div>
              : null
              }
            </div>
            }
          </section>
        </div>
      </ModuleCertificateLayout>
    )
  }
}

ModuleCertificateList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    courseStore: store.course
    , loggedInUser: store.user.loggedIn.user
    , moduleCertificateStore: store.moduleCertificate
    , contentModuleStore: store.contentModule
    , studyStore: store.study
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(ModuleCertificateList)
);



// WEBPACK FOOTER //
// modules/moduleCertificate/views/ModuleCertificateList.js.jsx